import {
  Stack,
  Tooltip,
  Typography,
  type TypographyProps,
  styled,
  useTheme,
} from "@mui/material"
import type { ReactNode } from "react"
import { Info } from "react-feather"
import { formatDateWithDots } from "../../../../../services"
import { type SourceName, sourceEnum } from "../../../../../types"
import { labelOf, pluralize } from "../../../../../utils"

export interface AsideSectionSummaryInfoProps {
  sources?: SourceName[]
  period?: string | null
  ToolTipContent?: ReactNode
  showSourcesWhenEmpty?: boolean
  dateLabel?: string
}

const fontSizeCardInfo = 12

export const AsideSectionSummaryInfo = ({
  sources = [],
  period,
  ToolTipContent,
  showSourcesWhenEmpty = true,
  dateLabel = "Mise à jour le : ",
}: AsideSectionSummaryInfoProps) => {
  const {
    palette: { text },
    typography: { pxToRem },
  } = useTheme()

  return (
    <Stack direction="row" alignItems="center" gap={1}>
      <AsideSectionSummaryInfoTypo>
        <SourcesLabel
          sources={sources}
          showSourcesWhenEmpty={showSourcesWhenEmpty}
        />
        {dateLabel}
        {period ? formatDateWithDots(period) : "-"}
      </AsideSectionSummaryInfoTypo>
      {ToolTipContent && (
        <Tooltip title={ToolTipContent}>
          <Info size={pxToRem(fontSizeCardInfo + 2)} color={text.disabled} />
        </Tooltip>
      )}
    </Stack>
  )
}

const SourcesLabel = ({
  sources,
  showSourcesWhenEmpty,
}: { sources: SourceName[]; showSourcesWhenEmpty: boolean }) => {
  if (sources.length === 0 && !showSourcesWhenEmpty) {
    return null
  }

  const sourcesLabel =
    sources.length > 0
      ? sources.map((source) => labelOf(sourceEnum, source)).join(", ")
      : "-"
  return (
    <>
      {pluralize(sources.length, "Source", "Sources")} :{" "}
      <AsideSectionSummaryInfoTypoBold component="span" mr={1}>
        {sourcesLabel}
      </AsideSectionSummaryInfoTypoBold>{" "}
    </>
  )
}

const AsideSectionSummaryInfoTypo = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.pxToRem(fontSizeCardInfo),
  color: theme.palette.text.disabled,
  textWrap: "nowrap",
}))

const AsideSectionSummaryInfoTypoBold = styled(Typography)<TypographyProps>(
  ({ theme }) => ({
    fontSize: theme.typography.pxToRem(fontSizeCardInfo),
    color: theme.palette.primary.main,
    fontWeight: 500,
    textWrap: "nowrap",
  }),
)

AsideSectionSummaryInfoTypoBold.defaultProps = {
  component: "span",
  children: "-",
}
